/**
 * frontend/src/hooks/useApiEvents.js
 * =====================================
 * Subscribes to the custom API events emitted by api.js interceptors.
 * Returns state that components use to render rate-limit countdowns
 * and plan upgrade prompts.
 *
 * Mount this once in ManagerLayout — it wraps the entire dashboard surface.
 * CheckIn.jsx handles its own 429/403 inline since it's a separate surface.
 *
 * Usage:
 *   const { rateLimitBanner, planGateModal, clearPlanGate } = useApiEvents()
 */

import { useCallback, useEffect, useState } from 'react'
import { API_EVENTS } from '../lib/api'

export function useApiEvents() {
  const [rateLimitBanner, setRateLimitBanner] = useState(null)
  // { retryAfter: number, secondsLeft: number, path: string }

  const [planGateModal, setPlanGateModal] = useState(null)
  // { code, message, current_plan, required_plan }

  // ── Rate limit countdown ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const { retryAfter, path } = e.detail
      setRateLimitBanner({ retryAfter, secondsLeft: retryAfter, path })
    }
    window.addEventListener(API_EVENTS.RATE_LIMITED, handler)
    return () => window.removeEventListener(API_EVENTS.RATE_LIMITED, handler)
  }, [])

  // Tick the countdown down every second
  useEffect(() => {
    if (!rateLimitBanner) return
    if (rateLimitBanner.secondsLeft <= 0) {
      setRateLimitBanner(null)
      return
    }
    const timer = setTimeout(() => {
      setRateLimitBanner((b) =>
        b ? { ...b, secondsLeft: b.secondsLeft - 1 } : null
      )
    }, 1000)
    return () => clearTimeout(timer)
  }, [rateLimitBanner])

  // ── Plan gate ───────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => setPlanGateModal(e.detail)
    window.addEventListener(API_EVENTS.PLAN_GATE, handler)
    return () => window.removeEventListener(API_EVENTS.PLAN_GATE, handler)
  }, [])

  const clearPlanGate = useCallback(() => setPlanGateModal(null), [])

  return { rateLimitBanner, planGateModal, clearPlanGate }
}
